// src/pages/LineupPage.jsx
// Weekly lineup: 1 WK + 3 BAT + 3 BOWL + 2 ALR + 2 FLEX
// WK can fill BAT slots. BAT cannot fill WK slots.
// Lineup locks once weekStartTime passes.
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { auth, db } from "../firebase";
import { ref, onValue, get, update } from "firebase/database";
import { IPL_TEAMS } from "../players";
import { FANTASY_WEEKS } from "../services/iplSchedule";

function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

// Slot definitions: slotId, label, eligibleRoles
const SLOT_DEFINITIONS = [
  { id: "wk1",   label: "WK",     eligible: ["WK"]                           },
  { id: "bat1",  label: "BAT 1",  eligible: ["BAT", "WK"]                    },
  { id: "bat2",  label: "BAT 2",  eligible: ["BAT", "WK"]                    },
  { id: "bat3",  label: "BAT 3",  eligible: ["BAT", "WK"]                    },
  { id: "bowl1", label: "BOWL 1", eligible: ["BOWL"]                          },
  { id: "bowl2", label: "BOWL 2", eligible: ["BOWL"]                          },
  { id: "bowl3", label: "BOWL 3", eligible: ["BOWL"]                          },
  { id: "ar1",   label: "AR 1",   eligible: ["ALR", "ALLROUND"]               },
  { id: "ar2",   label: "AR 2",   eligible: ["ALR", "ALLROUND"]               },
  { id: "flex1", label: "FLEX 1", eligible: ["BAT","BOWL","ALR","ALLROUND","WK"] },
  { id: "flex2", label: "FLEX 2", eligible: ["BAT","BOWL","ALR","ALLROUND","WK"] },
];

// Normalise role — handles legacy "ALLROUND" stored in Firebase before rename
function normaliseRole(role) {
  if (!role) return "";
  if (role === "ALLROUND") return "ALR";
  return role;
}

const SLOT_LABELS = {
  wk1:   { short: "WK",   color: "var(--purple)" },
  bat1:  { short: "BAT",  color: "var(--blue)"   },
  bat2:  { short: "BAT",  color: "var(--blue)"   },
  bat3:  { short: "BAT",  color: "var(--blue)"   },
  bowl1: { short: "BOWL", color: "var(--green)"  },
  bowl2: { short: "BOWL", color: "var(--green)"  },
  bowl3: { short: "BOWL", color: "var(--green)"  },
  ar1:   { short: "AR",   color: "var(--gold)"   },
  ar2:   { short: "AR",   color: "var(--gold)"   },
  flex1: { short: "FLEX", color: "var(--text-dim)" },
  flex2: { short: "FLEX", color: "var(--text-dim)" },
};

function canFillSlot(player, slotDef) {
  const role = normaliseRole(player.role);
  return slotDef.eligible.includes(role) || slotDef.eligible.includes(player.role);
}

// Returns slot errors: array of slot ids that are empty or have wrong role
function validateLineup(slotAssignments, playerMap) {
  const errors = [];
  for (const slotDef of SLOT_DEFINITIONS) {
    const pid = slotAssignments[slotDef.id];
    if (!pid) { errors.push(slotDef.id); continue; }
    const player = playerMap[String(pid)];
    if (!player || !canFillSlot(player, slotDef)) errors.push(slotDef.id);
  }
  return errors;
}

function SlotRow({ slotDef, assignedPlayer, onAssign, onClear, isCaptain, isVC, onCaptain, onVC, eligibleBench, locked = false }) {
  const [open, setOpen] = useState(false);
  const slotMeta = SLOT_LABELS[slotDef.id];
  const isEmpty = !assignedPlayer;

  return (
    <div style={{ borderBottom: "1px solid var(--border)" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10, padding: "9px 14px",
        background: isEmpty ? "rgba(248,113,113,0.04)" : isCaptain ? "rgba(245,166,35,0.08)" : isVC ? "rgba(96,165,250,0.06)" : "transparent",
      }}>
        {/* Slot type badge */}
        <div style={{
          width: 42, fontSize: 9, fontWeight: 800, textAlign: "center",
          padding: "2px 0", borderRadius: 4, flexShrink: 0,
          background: isEmpty ? "rgba(248,113,113,0.15)" : "rgba(255,255,255,0.05)",
          color: isEmpty ? "var(--red)" : slotMeta.color,
          letterSpacing: "0.06em",
        }}>
          {slotMeta.short}
        </div>

        {/* Player or empty state */}
        {assignedPlayer ? (
          <>
            {/* C / VC buttons */}
            <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
              <button onClick={() => onCaptain(assignedPlayer.id)} style={{
                width: 20, height: 20, padding: 0, borderRadius: "50%", fontSize: 8, fontWeight: 900,
                background: isCaptain ? "var(--gold)" : "var(--bg2)",
                color: isCaptain ? "#000" : "var(--text-muted)",
                border: `1px solid ${isCaptain ? "var(--gold)" : "var(--border)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>C</button>
              <button onClick={() => onVC(assignedPlayer.id)} style={{
                width: 20, height: 20, padding: 0, borderRadius: "50%", fontSize: 8, fontWeight: 900,
                background: isVC ? "var(--blue)" : "var(--bg2)",
                color: isVC ? "#000" : "var(--text-muted)",
                border: `1px solid ${isVC ? "var(--blue)" : "var(--border)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>V</button>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                color: isCaptain ? "var(--gold)" : isVC ? "var(--blue)" : "var(--text)",
              }}>
                {assignedPlayer.name}
                {isCaptain && <span className="pill" style={{ marginLeft: 5, fontSize: 8 }}>2×</span>}
                {isVC && <span className="pill blue" style={{ marginLeft: 5, fontSize: 8 }}>1.5×</span>}
              </div>
              <div style={{ display: "flex", gap: 5, marginTop: 2 }}>
                <span className={`role-chip role-${assignedPlayer.role}`}>{assignedPlayer.role}</span>
                <span className="team-chip" style={{ color: IPL_TEAMS[assignedPlayer.team]?.color || "var(--text-dim)" }}>
                  {assignedPlayer.team}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="mini" onClick={() => setOpen(v => !v)} style={{ fontSize: 11 }} disabled={locked}>
                {open ? "▲" : "Swap"}
              </button>
              <button className="mini" onClick={() => onClear(slotDef.id)} style={{ fontSize: 11, color: "var(--red)" }} disabled={locked}>
                ✕
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ flex: 1, fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
              Empty — select a player
            </div>
            <button className="mini" onClick={() => setOpen(v => !v)} style={{ fontSize: 11, color: "var(--gold)" }} disabled={locked}>
              {open ? "▲ Close" : "＋ Pick"}
            </button>
          </>
        )}
      </div>

      {/* Dropdown picker */}
      {open && (
        <div style={{
          background: "var(--bg2)", borderTop: "1px solid var(--border)",
          maxHeight: 200, overflowY: "auto",
        }}>
          {eligibleBench.length === 0 ? (
            <div style={{ padding: "10px 14px", fontSize: 12, color: "var(--text-muted)" }}>
              No eligible bench players available
            </div>
          ) : (
            eligibleBench.map(p => (
              <div
                key={p.id}
                onClick={() => { onAssign(slotDef.id, p.id); setOpen(false); }}
                style={{
                  padding: "8px 14px", cursor: "pointer", display: "flex",
                  alignItems: "center", gap: 8,
                  borderBottom: "1px solid var(--border)",
                  transition: "background 0.1s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <span className={`role-chip role-${normaliseRole(p.role)}`}>{normaliseRole(p.role)}</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</span>
                <span className="team-chip" style={{ marginLeft: "auto", color: IPL_TEAMS[p.team]?.color }}>
                  {p.team}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Compact inline dropdown for ESPN-style table
function SlotDropdown({ slotDef, current, eligibleBench, onAssign }) {
  const [open, setOpen] = useState(false);
  const ref_ = React.useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref_.current && !ref_.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref_} style={{ position: "relative" }}>
      <button className="mini" onClick={() => setOpen(v => !v)} type="button"
        style={{ fontSize: 10, padding: "3px 8px", color: "var(--gold)", borderColor: "rgba(245,166,35,0.3)" }}>
        {current ? "Swap" : "+ Pick"}
      </button>
      {open && (
        <div style={{
          position: "absolute", right: 0, top: "100%", zIndex: 100,
          background: "var(--bg3)", border: "1px solid var(--border2)",
          borderRadius: "var(--radius)", minWidth: 200, maxHeight: 220,
          overflowY: "auto", boxShadow: "var(--shadow-lg)",
        }}>
          {eligibleBench.length === 0 ? (
            <div style={{ padding: "10px 14px", fontSize: 12, color: "var(--text-muted)" }}>No eligible players</div>
          ) : eligibleBench.map(p => (
            <div key={p.id} onClick={() => { onAssign(slotDef.id, p.id); setOpen(false); }}
              style={{ padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                borderBottom: "1px solid var(--border)", fontSize: 12 }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span className={`role-chip role-${normaliseRole(p.role)}`} style={{ fontSize: 8 }}>{p.role}</span>
              <span style={{ flex: 1, fontWeight: 600 }}>{p.name}</span>
              <span style={{ fontSize: 10, color: IPL_TEAMS[p.team]?.color }}>{p.team}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function LineupPage({ leagueCode }) {
  const user = auth.currentUser;
  const uid  = user?.uid;
  const now  = useNow(10000); // refresh every 10s for countdown accuracy

  const [league,     setLeague]     = useState(null);
  const [room,       setRoom]       = useState(null);
  const [week,       setWeek]       = useState(1);
  const [saving,     setSaving]     = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [editingTeam, setEditingTeam] = useState(null); // host override: null = own team

  // slotAssignments: { slotId -> playerId }
  const [slotAssignments, setSlotAssignments] = useState({});
  const [captainId,       setCaptainId]       = useState(null);
  const [vcId,            setVcId]            = useState(null);

  const weekStartTime    = league?.season?.weekStartTime ?? null;
  const currentLeagueWeek = league?.season?.week ?? 1;
  // Past weeks: always locked. Current week: locked once deadline passes.
  // Future weeks: always editable (no deadline set yet).
  const isLocked = week < currentLeagueWeek
    ? true
    : week === currentLeagueWeek
      ? (weekStartTime ? now >= weekStartTime : false)
      : false;

  // Deadline countdown values
  const msLeft = weekStartTime && !isLocked ? Math.max(0, weekStartTime - now) : null;
  const hoursLeft = msLeft !== null ? msLeft / 3600000 : null;

  // Find window open time (previous week's start from FANTASY_WEEKS)
  const currentWeekInfo = FANTASY_WEEKS.find(w => w.startTime === weekStartTime);
  const prevWeekInfo    = currentWeekInfo
    ? FANTASY_WEEKS.find(w => w.week === currentWeekInfo.week - 1)
    : null;
  const windowOpenTime = prevWeekInfo?.startTime ?? (weekStartTime ? weekStartTime - 7 * 86400000 : null);
  const windowDuration = weekStartTime && windowOpenTime ? weekStartTime - windowOpenTime : null;
  const elapsed        = windowOpenTime ? Math.max(0, now - windowOpenTime) : null;
  const progressPct    = windowDuration && elapsed !== null
    ? Math.min(100, Math.round((elapsed / windowDuration) * 100))
    : null;

  const deadlineColor  = !weekStartTime ? "var(--text-dim)"
    : isLocked             ? "var(--red)"
    : hoursLeft < 2        ? "var(--red)"
    : hoursLeft < 24       ? "var(--gold)"
    : "var(--green)";

  const formatTimeLeft = () => {
    if (!msLeft) return null;
    const d = Math.floor(msLeft / 86400000);
    const h = Math.floor((msLeft % 86400000) / 3600000);
    const m = Math.ceil((msLeft % 3600000) / 60000);
    if (d >= 1) return `${d}d ${h}h remaining`;
    if (h >= 1) return `${h}h ${m}m remaining`;
    return `${m}m remaining`;
  };

  useEffect(() => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    const u1 = onValue(ref(db, `leagues/${code}`), s => setLeague(s.val() || null));
    const u2 = onValue(ref(db, `rooms/${code}`),   s => setRoom(s.val() || null));
    return () => { u1(); u2(); };
  }, [leagueCode]);

  useEffect(() => {
    if (league?.season?.week) setWeek(league.season.week);
  }, [league?.season?.week]);

  const mySlot = useMemo(() => {
    if (!uid || !room?.slots) return null;
    const entry = Object.entries(room.slots).find(([, v]) => v?.claimedBy === uid);
    return entry ? entry[0] : null;
  }, [uid, room?.slots]);

  const isHost = useMemo(() =>
    !!(uid && (room?.hostId === uid || league?.ownerUid === uid)),
    [uid, room?.hostId, league?.ownerUid]
  );

  // Which team's lineup is being edited (own or host override)
  const activeSlot = editingTeam || mySlot;

  const activeRoster = useMemo(() => {
    if (!activeSlot || !room) return [];
    const teamList = Array.isArray(room.teams) ? room.teams : Object.values(room.teams || {});
    const team = teamList.find(t => t.name === activeSlot);
    if (!team?.squadIds?.length) return [];
    const pm = room.players || {};
    return team.squadIds.map(id => pm[String(id)]).filter(Boolean);
  }, [activeSlot, room]);

  // Keep myRoster for the host auto-fill effect (own team only)
  const myRoster = useMemo(() => {
    if (!mySlot || !room) return [];
    const teamList = Array.isArray(room.teams) ? room.teams : Object.values(room.teams || {});
    const team = teamList.find(t => t.name === mySlot);
    if (!team?.squadIds?.length) return [];
    const pm = room.players || {};
    return team.squadIds.map(id => pm[String(id)]).filter(Boolean);
  }, [mySlot, room]);

  const allTeams = useMemo(() =>
    Array.isArray(room?.teams) ? room.teams : Object.values(room?.teams || {}),
    [room?.teams]
  );

  const playerMap = useMemo(() => room?.players || {}, [room?.players]);

  // Live points for this week (for showing in lineup table)
  const playerPointsThisWeek = useMemo(() =>
    league?.season?.playerPoints?.[String(week)] || {},
    [league, week]
  );

  // Load saved lineup — carries forward from previous week if not set.
  // `cancelled` prevents a stale run (week=1 initial state) from overwriting
  // the correct week's data when the real week loads shortly after.
  useEffect(() => {
    if (!leagueCode || !activeSlot || !activeRoster.length) return;
    const code = leagueCode.toUpperCase();
    let cancelled = false;

    // Host editing another team: just load current data or auto-fill fresh (no carry-forward)
    if (editingTeam && editingTeam !== mySlot) {
      get(ref(db, `leagues/${code}/season/lineups/${week}/${editingTeam}`)).then(snap => {
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.val();
          setSlotAssignments(data.slotAssignments || {});
          setCaptainId(data.captainId || null);
          setVcId(data.vcId || null);
        } else {
          const autoAssign = {};
          const used = new Set();
          for (const slotDef of SLOT_DEFINITIONS) {
            const cand = activeRoster.find(p => !used.has(String(p.id)) && canFillSlot(p, slotDef));
            if (cand) { autoAssign[slotDef.id] = cand.id; used.add(String(cand.id)); }
          }
          const starters = Object.values(autoAssign).map(String);
          const batAlr = activeRoster.filter(p => starters.includes(String(p.id)) && ["BAT","ALR","ALLROUND"].includes(normaliseRole(p.role)));
          const capId = batAlr[0]?.id ?? starters[0] ?? null;
          const vcIdVal = batAlr[1]?.id ?? starters[1] ?? null;
          setSlotAssignments(autoAssign);
          setCaptainId(capId ? String(capId) : null);
          setVcId(vcIdVal ? String(vcIdVal) : null);
        }
      });
      return () => { cancelled = true; };
    }

    const applyLineup = (data) => {
      setSlotAssignments(data.slotAssignments || {});
      setCaptainId(data.captainId || null);
      setVcId(data.vcId || null);
    };

    const autoFill = () => {
      // Greedy auto-fill by role priority
      const autoAssign = {};
      const used = new Set();
      for (const slotDef of SLOT_DEFINITIONS) {
        const candidate = activeRoster.find(p => !used.has(p.id) && canFillSlot(p, slotDef));
        if (candidate) { autoAssign[slotDef.id] = candidate.id; used.add(candidate.id); }
      }
      // Auto-pick captain = highest value player (first BAT or ALR), VC = second
      const starters = Object.values(autoAssign);
      const batAlr = activeRoster.filter(p => starters.includes(p.id) && (p.role === "BAT" || p.role === "ALR" || p.role === "ALLROUND"));
      const capId = batAlr[0]?.id ?? starters[0] ?? null;
      const vcIdVal = batAlr[1]?.id ?? starters[1] ?? null;
      setSlotAssignments(autoAssign);
      setCaptainId(capId ? String(capId) : null);
      setVcId(vcIdVal ? String(vcIdVal) : null);
      return { slotAssignments: autoAssign, captainId: capId ? String(capId) : null, vcId: vcIdVal ? String(vcIdVal) : null };
    };

    get(ref(db, `leagues/${code}/season/lineups/${week}/${activeSlot}`)).then(snap => {
      if (cancelled) return;
      if (snap.exists()) {
        const data = snap.val();
        const hasSlots = data.slotAssignments && Object.keys(data.slotAssignments).length > 0;
        if (hasSlots) {
          // Real saved lineup — use it
          applyLineup(data);
          return;
        }
        // Exists but empty (created by season init) — fall through to carry/autofill
      }
      // Try to carry forward from the previous week
      const prevWeek = week - 1;
      if (prevWeek >= 1) {
        get(ref(db, `leagues/${code}/season/lineups/${prevWeek}/${activeSlot}`)).then(prevSnap => {
          if (cancelled) return;
          if (prevSnap.exists()) {
            const prev = prevSnap.val();
            const prevHasSlots = prev.slotAssignments && Object.keys(prev.slotAssignments).length > 0;
            if (prevHasSlots) {
              // Validate carried-forward assignments are still on the roster
              const validSlots = {};
              const rosterIds = new Set(activeRoster.map(p => String(p.id)));
              Object.entries(prev.slotAssignments || {}).forEach(([slotId, pid]) => {
                if (rosterIds.has(String(pid))) validSlots[slotId] = pid;
              });
              const capId = rosterIds.has(String(prev.captainId)) ? prev.captainId : null;
              const vcIdVal = rosterIds.has(String(prev.vcId)) ? prev.vcId : null;
              const carried = { slotAssignments: validSlots, captainId: capId, vcId: vcIdVal };
              applyLineup(carried);
              const starters = Object.values(validSlots).filter(Boolean);
              update(ref(db), {
                [`leagues/${code}/season/lineups/${week}/${activeSlot}/slotAssignments`]: validSlots,
                [`leagues/${code}/season/lineups/${week}/${activeSlot}/starters`]: starters,
                [`leagues/${code}/season/lineups/${week}/${activeSlot}/captainId`]: capId,
                [`leagues/${code}/season/lineups/${week}/${activeSlot}/vcId`]: vcIdVal,
                [`leagues/${code}/season/lineups/${week}/${activeSlot}/carriedFrom`]: prevWeek,
              }).catch(() => {});
              return;
            }
          }
          // No previous week with slots — fresh auto-fill
          if (cancelled) return;
          const filled = autoFill();
          const starters = Object.values(filled.slotAssignments).filter(Boolean);
          update(ref(db), {
            [`leagues/${code}/season/lineups/${week}/${activeSlot}/slotAssignments`]: filled.slotAssignments,
            [`leagues/${code}/season/lineups/${week}/${activeSlot}/starters`]: starters,
            [`leagues/${code}/season/lineups/${week}/${activeSlot}/captainId`]: filled.captainId,
            [`leagues/${code}/season/lineups/${week}/${activeSlot}/vcId`]: filled.vcId,
            [`leagues/${code}/season/lineups/${week}/${activeSlot}/autoFilled`]: true,
          }).catch(() => {});
        });
      } else {
        // Week 1, no previous — fresh auto-fill and save
        if (cancelled) return;
        const filled = autoFill();
        const starters = Object.values(filled.slotAssignments).filter(Boolean);
        update(ref(db), {
          [`leagues/${code}/season/lineups/${week}/${activeSlot}/slotAssignments`]: filled.slotAssignments,
          [`leagues/${code}/season/lineups/${week}/${activeSlot}/starters`]: starters,
          [`leagues/${code}/season/lineups/${week}/${activeSlot}/captainId`]: filled.captainId,
          [`leagues/${code}/season/lineups/${week}/${activeSlot}/vcId`]: filled.vcId,
          [`leagues/${code}/season/lineups/${week}/${activeSlot}/autoFilled`]: true,
        }).catch(() => {});
      }
    });

    return () => { cancelled = true; };
  }, [leagueCode, activeSlot, week, activeRoster.length, editingTeam]); // eslint-disable-line

  // When the host views this page, silently auto-fill any team that has no
  // lineup set for the current week.  Non-hosts never trigger this path.
  useEffect(() => {
    if (!isHost || !room || !leagueCode) return;
    const code = leagueCode.toUpperCase();
    // Firebase can return arrays as objects — handle both
    const teams  = Array.isArray(room.teams) ? room.teams : Object.values(room.teams || {});
    const players = room.players || {};

    for (const team of teams) {
      const teamName = team.name;
      const teamRoster = (team.squadIds || [])
        .map(id => players[String(id)]).filter(Boolean);
      if (!teamRoster.length) continue;

      get(ref(db, `leagues/${code}/season/lineups/${week}/${teamName}`)).then(snap => {
        if (snap.exists()) {
          const data = snap.val();
          if (data.slotAssignments && Object.keys(data.slotAssignments).length > 0) return;
        }

        const rosterIds = new Set(teamRoster.map(p => String(p.id)));

        const saveLineup = (slotAssignments, capId, vcIdVal, meta) => {
          const starters = Object.values(slotAssignments).map(String);
          update(ref(db), {
            [`leagues/${code}/season/lineups/${week}/${teamName}/slotAssignments`]: slotAssignments,
            [`leagues/${code}/season/lineups/${week}/${teamName}/starters`]:        starters,
            [`leagues/${code}/season/lineups/${week}/${teamName}/captainId`]:       capId   ? String(capId)   : null,
            [`leagues/${code}/season/lineups/${week}/${teamName}/vcId`]:            vcIdVal ? String(vcIdVal) : null,
            ...meta,
          }).catch(() => {});
        };

        const freshAutoFill = () => {
          const autoAssign = {};
          const used = new Set();
          for (const slotDef of SLOT_DEFINITIONS) {
            const candidate = teamRoster.find(p => !used.has(String(p.id)) && canFillSlot(p, slotDef));
            if (candidate) { autoAssign[slotDef.id] = candidate.id; used.add(String(candidate.id)); }
          }
          const starters = Object.values(autoAssign).map(String);
          const batAlr = teamRoster.filter(p =>
            starters.includes(String(p.id)) &&
            ["BAT", "ALR", "ALLROUND"].includes(normaliseRole(p.role))
          );
          const capId   = batAlr[0]?.id ?? (starters[0] ?? null);
          const vcIdVal = batAlr[1]?.id ?? (starters[1] ?? null);
          saveLineup(autoAssign, capId, vcIdVal, {
            [`leagues/${code}/season/lineups/${week}/${teamName}/autoFilled`]: true,
          });
        };

        // Try to carry forward from the previous week, preserving captain/VC
        const prevWeek = week - 1;
        if (prevWeek >= 1) {
          get(ref(db, `leagues/${code}/season/lineups/${prevWeek}/${teamName}`)).then(prevSnap => {
            if (prevSnap.exists()) {
              const prev = prevSnap.val();
              const prevHasSlots = prev.slotAssignments && Object.keys(prev.slotAssignments).length > 0;
              if (prevHasSlots) {
                const validSlots = {};
                Object.entries(prev.slotAssignments || {}).forEach(([slotId, pid]) => {
                  if (rosterIds.has(String(pid))) validSlots[slotId] = pid;
                });
                const capId   = rosterIds.has(String(prev.captainId)) ? prev.captainId : null;
                const vcIdVal = rosterIds.has(String(prev.vcId))      ? prev.vcId      : null;
                saveLineup(validSlots, capId, vcIdVal, {
                  [`leagues/${code}/season/lineups/${week}/${teamName}/carriedFrom`]: prevWeek,
                });
                return;
              }
            }
            freshAutoFill();
          });
        } else {
          freshAutoFill();
        }
      });
    }
  }, [isHost, leagueCode, week, Object.keys(room?.teams || {}).length]); // eslint-disable-line

  // Players currently in a slot
  const assignedIds = useMemo(() => new Set(Object.values(slotAssignments).filter(Boolean)), [slotAssignments]);

  // Bench = roster players not assigned to any slot
  const bench = useMemo(() => activeRoster.filter(p => !assignedIds.has(p.id)), [activeRoster, assignedIds]);

  const assignPlayer = useCallback((slotId, playerId) => {
    setSlotAssignments(prev => {
      const next = { ...prev };
      // If player is already in another slot, swap them out first
      const existingSlot = Object.entries(next).find(([, v]) => v === playerId)?.[0];
      if (existingSlot) delete next[existingSlot];
      // If the target slot already has someone, move them to bench (just remove)
      next[slotId] = playerId;
      return next;
    });
    setSaveStatus("");
  }, []);

  const clearSlot = useCallback((slotId) => {
    setSlotAssignments(prev => {
      const next = { ...prev };
      const pid = next[slotId];
      delete next[slotId];
      if (captainId === pid) setCaptainId(null);
      if (vcId === pid) setVcId(null);
      return next;
    });
    setSaveStatus("");
  }, [captainId, vcId]);

  const handleCaptain = useCallback((playerId) => {
    setCaptainId(prev => prev === playerId ? null : playerId);
    if (vcId === playerId) setVcId(null);
    setSaveStatus("");
  }, [vcId]);

  const handleVC = useCallback((playerId) => {
    setVcId(prev => prev === playerId ? null : playerId);
    if (captainId === playerId) setCaptainId(null);
    setSaveStatus("");
  }, [captainId]);

  const slotErrors = useMemo(() => validateLineup(slotAssignments, playerMap), [slotAssignments, playerMap]);
  const filledCount = SLOT_DEFINITIONS.filter(s => slotAssignments[s.id]).length;
  const isValid = slotErrors.length === 0 && captainId && vcId;

  // Auto-save whenever lineup changes (debounced 1s) — own team only; host uses explicit Save
  const autoSaveTimer = useRef(null);
  useEffect(() => {
    const ownTeam = !editingTeam || editingTeam === mySlot;
    if (!leagueCode || !activeSlot || effectiveLocked || !ownTeam || !Object.keys(slotAssignments).length) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      const code = leagueCode.toUpperCase();
      const starters = Object.values(slotAssignments).filter(Boolean);
      update(ref(db), {
        [`leagues/${code}/season/lineups/${week}/${activeSlot}/slotAssignments`]: slotAssignments,
        [`leagues/${code}/season/lineups/${week}/${activeSlot}/starters`]: starters,
        [`leagues/${code}/season/lineups/${week}/${activeSlot}/captainId`]: captainId || null,
        [`leagues/${code}/season/lineups/${week}/${activeSlot}/vcId`]: vcId || null,
        [`leagues/${code}/season/lineups/${week}/${activeSlot}/autoFilled`]: false,
      }).then(() => setSaveStatus("✓ Auto-saved")).catch(() => {});
    }, 1000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [slotAssignments, captainId, vcId]); // eslint-disable-line

  const isEditingOtherTeam = !!(editingTeam && editingTeam !== mySlot);
  // Hosts bypass the lock for all edits (own team or others) — commissioner power
  const effectiveLocked = isHost ? false : isLocked;

  const saveLineup = async () => {
    if (!leagueCode || !activeSlot) return;
    if (effectiveLocked) { setSaveStatus("Lineup is locked — week has started."); return; }
    if (slotErrors.length > 0) { setSaveStatus(`Fill all ${slotErrors.length} empty/invalid slots.`); return; }
    if (!captainId) { setSaveStatus("Pick a Captain (C)."); return; }
    if (!vcId)      { setSaveStatus("Pick a Vice-Captain (V)."); return; }
    setSaving(true);
    setSaveStatus("Saving…");
    const code = leagueCode.toUpperCase();
    // Starters = all assigned player ids (for backwards compat with scoring)
    const starters = Object.values(slotAssignments).filter(Boolean);
    try {
      await update(ref(db), {
        [`leagues/${code}/season/lineups/${week}/${activeSlot}/slotAssignments`]: slotAssignments,
        [`leagues/${code}/season/lineups/${week}/${activeSlot}/starters`]: starters,
        [`leagues/${code}/season/lineups/${week}/${activeSlot}/captainId`]: captainId,
        [`leagues/${code}/season/lineups/${week}/${activeSlot}/vcId`]: vcId,
        [`leagues/${code}/season/lineups/${week}/${activeSlot}/lockedAt`]: Date.now(),
        ...(isEditingOtherTeam ? { [`leagues/${code}/season/lineups/${week}/${activeSlot}/hostEdited`]: true } : {}),
      });
      setSaveStatus(isEditingOtherTeam ? `✓ Saved ${editingTeam}'s lineup!` : "✓ Lineup saved!");
    } catch (e) {
      setSaveStatus("Save failed: " + (e?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  const loadPrevWeek = useCallback(async () => {
    if (!leagueCode || !activeSlot || week <= 1) return;
    const code = leagueCode.toUpperCase();
    const snap = await get(ref(db, `leagues/${code}/season/lineups/${week - 1}/${activeSlot}`));
    if (!snap.exists()) { setSaveStatus("No lineup found for previous week."); return; }
    const prev = snap.val();
    const rosterIds = new Set(activeRoster.map(p => String(p.id)));
    let validSlots = {};

    if (prev.slotAssignments && Object.keys(prev.slotAssignments).length > 0) {
      // New format: slot → playerId map
      Object.entries(prev.slotAssignments).forEach(([slotId, pid]) => {
        if (rosterIds.has(String(pid))) validSlots[slotId] = pid;
      });
    } else if (prev.starters && prev.starters.length > 0) {
      // Old format: flat starters array — reconstruct slot assignments by
      // fitting each player into the first eligible slot (same as auto-fill).
      const eligible = prev.starters.filter(pid => rosterIds.has(String(pid)));
      const used = new Set();
      for (const slotDef of SLOT_DEFINITIONS) {
        const match = eligible.find(pid => {
          if (used.has(String(pid))) return false;
          const p = activeRoster.find(r => String(r.id) === String(pid));
          return p && canFillSlot(p, slotDef);
        });
        if (match) { validSlots[slotDef.id] = match; used.add(String(match)); }
      }
    } else {
      setSaveStatus("Previous week had no lineup set.");
      return;
    }

    if (!Object.keys(validSlots).length) {
      setSaveStatus("Previous week's players are no longer on your roster.");
      return;
    }

    const capId   = rosterIds.has(String(prev.captainId)) ? prev.captainId : null;
    const vcIdVal = rosterIds.has(String(prev.vcId))      ? prev.vcId      : null;
    setSlotAssignments(validSlots);
    setCaptainId(capId   ? String(capId)   : null);
    setVcId(vcIdVal ? String(vcIdVal) : null);
    // Immediately persist so changes survive even if auto-save is suppressed.
    const starters = Object.values(validSlots).filter(Boolean);
    await update(ref(db), {
      [`leagues/${code}/season/lineups/${week}/${activeSlot}/slotAssignments`]: validSlots,
      [`leagues/${code}/season/lineups/${week}/${activeSlot}/starters`]:        starters,
      [`leagues/${code}/season/lineups/${week}/${activeSlot}/captainId`]:       capId   ? String(capId)   : null,
      [`leagues/${code}/season/lineups/${week}/${activeSlot}/vcId`]:            vcIdVal ? String(vcIdVal) : null,
    }).catch(() => {});
    setSaveStatus("✓ Wk " + (week - 1) + " lineup loaded");
  }, [leagueCode, activeSlot, week, activeRoster]); // eslint-disable-line

  if (!mySlot) {
    return (
      <div className="panel" style={{ padding: 24, textAlign: "center" }}>
        <div style={{ color: "var(--text-dim)" }}>You haven't joined a team slot in the draft room yet.</div>
        <div style={{ marginTop: 8, fontSize: 13, color: "var(--text-muted)" }}>Go to the Draft tab, join a slot, and your squad will appear here.</div>
      </div>
    );
  }
  if (!myRoster.length) {
    return (
      <div className="panel" style={{ padding: 24, textAlign: "center" }}>
        <div style={{ color: "var(--text-dim)" }}>No squad yet. Complete the draft first.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Host: team picker */}
      {isHost && allTeams.length > 0 && (
        <div className="panel" style={{ marginBottom: 8, padding: "10px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--gold)", letterSpacing: "0.06em", flexShrink: 0 }}>
              HOST — Edit lineup for:
            </span>
            {allTeams.map(team => {
              const isActive = activeSlot === team.name;
              const isOwn = team.name === mySlot;
              return (
                <button key={team.name} className="mini"
                  onClick={() => setEditingTeam(isOwn ? null : team.name)}
                  style={{
                    fontWeight: isActive ? 700 : 400,
                    color: isActive ? "var(--gold)" : "var(--text-dim)",
                    borderColor: isActive ? "var(--gold)" : "var(--border)",
                    background: isActive ? "rgba(245,166,35,0.08)" : "transparent",
                    fontSize: 11, padding: "3px 9px",
                  }}>
                  {team.name}{isOwn ? " (you)" : ""}
                </button>
              );
            })}
          </div>
          {isEditingOtherTeam && (
            <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>
              Editing <strong style={{ color: "var(--gold)" }}>{editingTeam}</strong>'s lineup.
              Lock is bypassed for host overrides.{" "}
              <button className="mini" onClick={() => setEditingTeam(null)}
                style={{ fontSize: 10, padding: "2px 7px", color: "var(--text-dim)" }}>
                ← Back to my lineup
              </button>
            </div>
          )}
        </div>
      )}

      {/* Deadline card — always visible */}
      <div className="lineup-deadline-card" style={{
        borderColor: isLocked ? "rgba(248,113,113,0.35)"
          : hoursLeft !== null && hoursLeft < 2 ? "rgba(248,113,113,0.35)"
          : hoursLeft !== null && hoursLeft < 24 ? "rgba(245,166,35,0.35)"
          : "var(--border)",
      }}>
        <div className="lineup-deadline-top">
          <div className="lineup-deadline-left">
            <span className="lineup-deadline-icon">
              {isLocked ? "🔒" : hoursLeft !== null && hoursLeft < 2 ? "⚠️" : "📅"}
            </span>
            <div>
              <div className="lineup-deadline-label">
                {isLocked ? "Lineup Locked" : "Lineup Deadline"}
              </div>
              {weekStartTime ? (
                <div className="lineup-deadline-date">
                  {new Date(weekStartTime).toLocaleDateString("en-GB", {
                    weekday: "short", day: "numeric", month: "short",
                    hour: "2-digit", minute: "2-digit",
                  })}
                  {currentWeekInfo && (
                    <span style={{ marginLeft: 8, color: "var(--text-muted)", fontSize: 11 }}>
                      · {currentWeekInfo.label}
                    </span>
                  )}
                </div>
              ) : (
                <div className="lineup-deadline-date" style={{ color: "var(--text-muted)" }}>
                  No deadline set — host hasn't synced yet
                </div>
              )}
            </div>
          </div>
          <div className="lineup-deadline-right">
            {isLocked ? (
              <span style={{ color: "var(--red)", fontWeight: 700, fontSize: 13 }}>
                Week in progress
              </span>
            ) : formatTimeLeft() ? (
              <div style={{ textAlign: "right" }}>
                <div className="lineup-countdown" style={{ color: deadlineColor }}>
                  {formatTimeLeft()}
                </div>
                {hoursLeft < 2 && (
                  <div style={{ fontSize: 11, color: "var(--red)", marginTop: 2 }}>
                    Save your lineup now!
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
        {/* Progress bar */}
        {progressPct !== null && (
          <div className="lineup-deadline-progress-wrap">
            <div className="lineup-deadline-progress-bar">
              <div className="lineup-deadline-progress-fill" style={{
                width: `${progressPct}%`,
                background: progressPct > 85 ? "var(--red)"
                  : progressPct > 60 ? "var(--gold)"
                  : "var(--green)",
              }} />
            </div>
            <div className="lineup-deadline-progress-labels">
              <span>Window open</span>
              <span style={{ color: deadlineColor, fontWeight: 600 }}>
                {isLocked ? "Locked" : `${progressPct}% elapsed`}
              </span>
              <span>Deadline</span>
            </div>
          </div>
        )}
      </div>

      {/* ESPN-style action bar */}
      <div className="action-bar">
        <div className="week-nav">
          <span className="label" style={{ marginRight: 4 }}>
            {isEditingOtherTeam ? `${editingTeam} — Wk:` : "Set Lineup:"}
          </span>
          {Array.from({ length: 12 }, (_, i) => i + 1).map(w => (
            <button key={w} className="mini" onClick={() => setWeek(w)}
              style={{ fontWeight: w === week ? 700 : 400, color: w === week ? "var(--gold)" : "var(--text-dim)", borderColor: w === week ? "var(--gold)" : "var(--border)", fontSize: 11, padding: "3px 8px" }}>
              Wk {w}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {saveStatus && <span style={{ fontSize: 12, color: saveStatus.startsWith("✓") || saveStatus.startsWith("Wk") ? "var(--green)" : "var(--red)" }}>{saveStatus}</span>}
          <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
            {filledCount}/11 filled
            {" · "}
            {captainId ? <span style={{ color: "var(--gold)" }}>C ✓</span> : <span style={{ color: "var(--red)" }}>No C</span>}
            {" · "}
            {vcId ? <span style={{ color: "var(--blue)" }}>VC ✓</span> : <span style={{ color: "var(--red)" }}>No VC</span>}
          </span>
          {week > 1 && !effectiveLocked && (
            <button className="mini" onClick={loadPrevWeek} type="button"
              style={{ fontSize: 11, padding: "4px 10px", color: "var(--text-dim)", borderColor: "var(--border)" }}>
              ← Prev week
            </button>
          )}
          <button className="primary" onClick={saveLineup} disabled={saving} style={{ padding: "6px 16px" }}>
            {saving ? "Saving…"
              : isEditingOtherTeam ? `Save ${editingTeam}`
              : "Save Lineup"}
          </button>
        </div>
      </div>

      {/* ESPN-style lineup table */}
      <div className="panel">
        <table className="lineup-table">
          <thead>
            <tr>
              <th style={{ width: 52 }}>SLOT</th>
              <th>PLAYER</th>
              <th className="center" style={{ width: 44 }}>C/VC</th>
              <th className="right hide-mobile" style={{ width: 60 }}>TEAM</th>
              <th className="right" style={{ width: 60 }}>PTS</th>
              <th className="right" style={{ width: 80 }}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {/* Starters section header */}
            <tr className="section-header">
              <td colSpan={6}>STARTERS — {filledCount} / 11 slots filled</td>
            </tr>

            {SLOT_DEFINITIONS.map(slotDef => {
              const pid = slotAssignments[slotDef.id];
              const player = pid ? playerMap[String(pid)] : null;
              const slotMeta = SLOT_LABELS[slotDef.id];
              const isCapt = captainId === pid;
              const isVc   = vcId === pid;
              const hasError = slotErrors.includes(slotDef.id);
              const eligibleBench = bench.filter(p => canFillSlot(p, slotDef) && p.acquiredWeek !== week);
              const pts = playerPointsThisWeek?.[String(pid)] ?? null;

              return (
                <tr key={slotDef.id} className={`starter-row${isCapt ? " captain-row" : ""}`}
                  style={{ background: hasError && !player ? "rgba(248,113,113,0.04)" : undefined }}>
                  {/* Slot badge */}
                  <td>
                    <span className="slot-badge" style={{ color: slotMeta.color, borderColor: slotMeta.color + "44" }}>
                      {slotMeta.short}
                    </span>
                  </td>

                  {/* Player cell */}
                  <td>
                    {player ? (
                      <div className="player-cell">
                        <div className="player-avatar-sm" style={{ background: `${IPL_TEAMS[player.team]?.color}18`, color: IPL_TEAMS[player.team]?.color, fontSize: 9, fontWeight: 900 }}>
                          {player.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                        </div>
                        <div className="player-name-cell">
                          <div className="name">{player.name}</div>
                          <div className="meta">
                            <span className={`role-chip role-${normaliseRole(player.role)}`} style={{ fontSize: 8 }}>{normaliseRole(player.role)}</span>
                            <span style={{ fontSize: 10, color: IPL_TEAMS[player.team]?.color }}>{player.team}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: hasError ? "var(--red)" : "var(--text-muted)", fontSize: 12, fontStyle: "italic" }}>
                        {eligibleBench.length > 0 ? "— pick a player —" : "Empty (no eligible players)"}
                      </div>
                    )}
                  </td>

                  {/* C / VC buttons */}
                  <td style={{ textAlign: "center" }}>
                    {player && !effectiveLocked && (
                      <div style={{ display: "flex", gap: 3, justifyContent: "center" }}>
                        <button onClick={() => handleCaptain(pid)} type="button"
                          style={{ width: 22, height: 22, padding: 0, fontSize: 9, fontWeight: 900, borderRadius: 11,
                            background: isCapt ? "var(--gold)" : "var(--bg2)", color: isCapt ? "#000" : "var(--text-dim)",
                            border: `1px solid ${isCapt ? "var(--gold)" : "var(--border)"}` }}>C</button>
                        <button onClick={() => handleVC(pid)} type="button"
                          style={{ width: 22, height: 22, padding: 0, fontSize: 9, fontWeight: 900, borderRadius: 11,
                            background: isVc ? "var(--blue)" : "var(--bg2)", color: isVc ? "#000" : "var(--text-dim)",
                            border: `1px solid ${isVc ? "var(--blue)" : "var(--border)"}` }}>V</button>
                      </div>
                    )}
                    {(isCapt || isVc) && effectiveLocked && (
                      <span style={{ fontSize: 11, fontWeight: 800, color: isCapt ? "var(--gold)" : "var(--blue)" }}>
                        {isCapt ? "C" : "VC"}
                      </span>
                    )}
                  </td>

                  {/* Team */}
                  <td className="stat-cell dim hide-mobile" style={{ fontSize: 11 }}>
                    {player ? player.team : "—"}
                  </td>

                  {/* Points */}
                  <td className="stat-cell">
                    {pts !== null ? (
                      <span style={{ color: pts >= 60 ? "var(--gold)" : pts >= 30 ? "var(--green)" : pts < 0 ? "var(--red)" : "var(--text)" }}>
                        {isCapt ? Math.round(pts * 2) : isVc ? Math.round(pts * 1.5) : pts}
                        {(isCapt || isVc) && <span style={{ fontSize: 8, marginLeft: 2, color: isCapt ? "var(--gold)" : "var(--blue)" }}>{isCapt ? "×2" : "×1.5"}</span>}
                      </span>
                    ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>

                  {/* Action */}
                  <td style={{ textAlign: "right" }}>
                    {!effectiveLocked && (
                      player ? (
                        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                          <SlotDropdown slotDef={slotDef} current={player} eligibleBench={eligibleBench} onAssign={assignPlayer} />
                          <button className="mini" onClick={() => clearSlot(slotDef.id)} type="button"
                            style={{ fontSize: 10, padding: "3px 7px", color: "var(--red)", borderColor: "rgba(248,113,113,0.3)" }}>✕</button>
                        </div>
                      ) : eligibleBench.length > 0 ? (
                        <SlotDropdown slotDef={slotDef} current={null} eligibleBench={eligibleBench} onAssign={assignPlayer} />
                      ) : (
                        <span style={{ fontSize: 10, color: "var(--text-muted)" }}>—</span>
                      )
                    )}
                  </td>
                </tr>
              );
            })}

            {/* Starters totals */}
            <tr className="totals-row">
              <td colSpan={4} style={{ fontSize: 12, color: "var(--text-dim)" }}>TOTALS</td>
              <td className="stat-cell gold">
                {Object.entries(slotAssignments).reduce((sum, [slotId, pid]) => {
                  const pts = playerPointsThisWeek?.[String(pid)] ?? 0;
                  const mult = pid === captainId ? 2 : pid === vcId ? 1.5 : 1;
                  return sum + pts * mult;
                }, 0).toFixed(0)}
              </td>
              <td />
            </tr>

            {/* Bench section */}
            <tr className="section-header">
              <td colSpan={6}>BENCH — {bench.length} players</td>
            </tr>
            {bench.length === 0 ? (
              <tr className="bench-row"><td colSpan={6} style={{ textAlign: "center", color: "var(--text-muted)", fontSize: 12, padding: 14 }}>All players assigned to Starting XI</td></tr>
            ) : bench.map(p => {
              const pts = playerPointsThisWeek?.[String(p.id)] ?? null;
              return (
                <tr key={p.id} className="bench-row">
                  <td><span className="slot-badge">BENCH</span></td>
                  <td>
                    <div className="player-cell">
                      <div className="player-avatar-sm" style={{ background: `${IPL_TEAMS[p.team]?.color}18`, color: IPL_TEAMS[p.team]?.color, fontSize: 9, fontWeight: 900 }}>
                        {p.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="player-name-cell">
                        <div className="name">{p.name}</div>
                        <div className="meta">
                          <span className={`role-chip role-${normaliseRole(p.role)}`} style={{ fontSize: 8 }}>{p.role}</span>
                          <span style={{ fontSize: 10, color: IPL_TEAMS[p.team]?.color }}>{p.team}</span>
                          {p.acquiredWeek === week && <span style={{ fontSize: 9, color: "var(--text-muted)", marginLeft: 4 }}>waiver — ineligible wk{week}</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td />
                  <td className="stat-cell dim hide-mobile" style={{ fontSize: 11 }}>{p.team}</td>
                  <td className="stat-cell dim">{pts !== null ? pts : "—"}</td>
                  <td />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Rules */}
      <div style={{ marginTop: 10, padding: "10px 14px", fontSize: 11, color: "var(--text-dim)", display: "flex", gap: 16, flexWrap: "wrap" }}>
        <span><span style={{ color: "var(--purple)", fontWeight: 700 }}>WK</span> — WK slot</span>
        <span><span style={{ color: "var(--blue)", fontWeight: 700 }}>BAT</span> — BAT or WK</span>
        <span><span style={{ color: "var(--green)", fontWeight: 700 }}>BOWL</span> — BOWL only</span>
        <span><span style={{ color: "var(--gold)", fontWeight: 700 }}>AR</span> — ALR only</span>
        <span><strong>FLEX</strong> — Any role</span>
        <span style={{ marginLeft: "auto" }}>C = 2× · VC = 1.5×</span>
      </div>
    </div>
  );
}
